import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import type { Collection, Doc } from "../api/types";
import { cx } from "../lib/cx";
import { formatAbsoluteTime, formatRelativeTime } from "../lib/time";
import { Menu, type MenuItem } from "../ui/Menu";

/**
 * One row of every document list in the product. Home's four tabs, drafts,
 * archive, trash, a collection tab and the backlinks panel all render this, so
 * the shape of a document row is decided once: icon, title, where it lives,
 * who touched it last, and how far through its checklist it is.
 */

export interface DocumentRowProps {
  document: Doc;
  /** Collection name for the breadcrumb, when the list spans collections. */
  collectionName?: string | undefined;
  /** Rendered instead of the collection breadcrumb, e.g. "deleted 2 days ago". */
  note?: string | undefined;
  actions?: MenuItem[] | undefined;
  /** Where the title links; defaults to the document's canonical URL. */
  to?: string | undefined;
}

export function TaskProgress({ document }: { document: Doc }): ReactNode {
  const tasks = document.tasks;
  if (tasks === undefined || tasks.total === 0) return null;
  const done = tasks.completed >= tasks.total;
  return (
    <span
      className={cx("shrink-0 text-xs", done ? "text-accent" : "text-muted")}
      aria-label={`${String(tasks.completed)} of ${String(tasks.total)} tasks complete`}
    >
      {tasks.completed}/{tasks.total}
    </span>
  );
}

export function DocumentIcon({ document }: { document: Doc }): ReactNode {
  return (
    <span aria-hidden="true" className="w-5 shrink-0 text-center">
      {document.icon ?? "📄"}
    </span>
  );
}

function badges(document: Doc): string[] {
  const result: string[] = [];
  if (document.template === true) result.push("Template");
  if (document.publishedAt === null && document.template !== true) result.push("Draft");
  if (document.archivedAt !== null) result.push("Archived");
  if (document.deletedAt !== null) result.push("Deleted");
  return result;
}

export function DocumentRow({
  document,
  collectionName,
  note,
  actions,
  to,
}: DocumentRowProps): ReactNode {
  const updatedBy = document.updatedBy?.name ?? document.createdBy?.name;
  const when = document.updatedAt ?? document.createdAt;

  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <DocumentIcon document={document} />
      <div className="min-w-0 flex-1">
        <Link to={to ?? document.url} className="text-sm font-medium hover:underline">
          {document.title === "" ? "Untitled" : document.title}
        </Link>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted">
          {badges(document).map((badge) => (
            <span key={badge} className="rounded border border-line px-1 py-px">
              {badge}
            </span>
          ))}
          {note !== undefined && <span>{note}</span>}
          {note === undefined && collectionName !== undefined && <span>{collectionName}</span>}
          {updatedBy !== undefined && <span>{updatedBy}</span>}
          {when !== undefined && (
            <time dateTime={when} title={formatAbsoluteTime(when)}>
              {formatRelativeTime(when)}
            </time>
          )}
        </p>
      </div>
      <TaskProgress document={document} />
      {actions !== undefined && actions.length > 0 && (
        <Menu
          buttonLabel={`Actions for ${document.title === "" ? "Untitled" : document.title}`}
          menuLabel="Document actions"
          items={actions}
          trigger="⋯"
        />
      )}
    </li>
  );
}

export interface DocumentListProps {
  label: string;
  documents: Doc[];
  /** Abilities by document id; row actions are filtered against them. */
  abilities?: Record<string, Record<string, boolean>>;
  collections?: Collection[];
  loading?: boolean;
  error?: Error | null;
  emptyMessage?: string;
  rowActions?: (document: Doc, abilities: Record<string, boolean>) => MenuItem[];
  rowNote?: (document: Doc) => string | undefined;
}

export function DocumentList({
  label,
  documents,
  abilities = {},
  collections = [],
  loading = false,
  error = null,
  emptyMessage = "Nothing here yet.",
  rowActions,
  rowNote,
}: DocumentListProps): ReactNode {
  const names = new Map(collections.map((collection) => [collection.id, collection.name]));

  return (
    <ul
      aria-label={label}
      aria-busy={loading}
      className="mt-6 divide-y divide-line rounded-md border border-line"
    >
      {error !== null ? (
        <li className="px-4 py-6 text-sm text-danger" role="alert">
          {error.message}
        </li>
      ) : loading ? (
        <li className="px-4 py-6 text-sm text-muted">Loading…</li>
      ) : documents.length === 0 ? (
        <li className="px-4 py-6 text-sm text-muted">{emptyMessage}</li>
      ) : (
        documents.map((document) => (
          <DocumentRow
            key={document.id}
            document={document}
            collectionName={
              document.collectionId == null ? undefined : names.get(document.collectionId)
            }
            note={rowNote?.(document)}
            actions={rowActions?.(document, abilities[document.id] ?? {})}
          />
        ))
      )}
    </ul>
  );
}
